// BUILTIN_CLASS_EXTENSIONS_FOCUS_V2
import { getEffectiveToday, isOpenEverything } from '../../../utils/systemTime';
import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

async function fetchQuestions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_questions?active=eq.true&select=*&order=question_order.asc`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  return response.json();
}

async function fetchCourseQuestion(courseId, section) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_self_evaluation_questions?course_id=eq.${courseId}&section=eq.${encodeURIComponent(section)}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data[0] || null;
}

async function fetchResponse(enrolmentId, section) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/self_evaluation_responses?enrolment_id=eq.${enrolmentId}&section=eq.${encodeURIComponent(section)}&select=*`,
    { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
  );
  const data = await response.json();
  return data[0] || null;
}


async function upsertResponse(data, existingResponse = null) {
  const updating = !!existingResponse;

  const filter = existingResponse?.id
    ? '?id=eq.' + encodeURIComponent(existingResponse.id)
    : '?enrolment_id=eq.' + encodeURIComponent(data.enrolment_id) +
      '&section=eq.' + encodeURIComponent(data.section);

  const response = await fetch(
    SUPABASE_URL + '/rest/v1/self_evaluation_responses' +
      (updating ? filter : ''),
    {
      method: updating ? 'PATCH' : 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': updating
          ? 'return=representation'
          : 'resolution=merge-duplicates,return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  const result = await response.json().catch(() => null);

  if (!response.ok) {
    const explanation =
      result?.message ||
      result?.error ||
      response.statusText ||
      'Database rejected the save';

    throw new Error(
      'Self-Evaluation was not saved (' +
      response.status + '): ' + explanation
    );
  }

  if (!Array.isArray(result) || !result[0]) {
    throw new Error(
      'The database did not confirm your Self-Evaluation was saved.'
    );
  }

  return result[0];
}

function addDaysToDateString(dateStr,days){
  if(!dateStr){
    return null;
  }

  const p=
    String(dateStr)
      .slice(0,10)
      .split('-')
      .map(Number);

  if(
    p.length!==3 ||
    !p.every(Number.isFinite)
  ){
    return dateStr;
  }

  const d=
    new Date(
      p[0],
      p[1]-1,
      p[2],
      12,
      0,
      0
    );

  d.setDate(
    d.getDate()+
    Math.max(
      0,
      Number(days)||0
    )
  );

  return [
    d.getFullYear(),
    String(
      d.getMonth()+1
    ).padStart(2,'0'),
    String(
      d.getDate()
    ).padStart(2,'0')
  ].join('-');
}


function getSectionCloseDate(
  course,
  section
){
  return addDaysToDateString(
    section==='Mid-Semester'
      ?course.mid_eval_close_date
      :course.final_eval_close_date,

    section==='Mid-Semester'
      ?course.mid_eval_extension_days
      :course.final_eval_extension_days
  );
}


function getSectionStatus(course, section, session) {
  if(isOpenEverything(session)){
    return 'open';
  }

  const today=
    getEffectiveToday(session);

  const open=
    section==='Mid-Semester'
      ?course.mid_eval_open_date
      :course.final_eval_open_date;

  const close=
    getSectionCloseDate(
      course,
      section
    );

  if(!open||!close){
    return 'not_configured';
  }

  if(today<open){
    return 'not_open';
  }

  return today<=close
    ?'open'
    :'closed';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function SelfEvalForm({ section, course, enrolment, questions, courseQuestion, existingResponse, onSaved }) {
  const [answers, setAnswers] = useState(existingResponse?.answers || {});
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function setAnswer(questionId, value, field = 'value') {
    setAnswers(prev => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value }
    }));
  }

  function validate() {
    for (const q of questions) {
      if (q.required) {
        const answer = answers[q.id];
        if (!answer?.value?.trim()) {
          setError(`Please answer: ${q.section_title}`);
          return false;
        }
        if (q.show_justification && q.justification_required && !answer?.justification?.trim()) {
          setError(`Please provide justification for: ${q.section_title}`);
          return false;
        }
      }
    }
    if (courseQuestion && courseQuestion.required !== false) {
      if (!answers['courseSpecific']?.value?.trim()) {
        setError('Please answer the course-specific question.');
        return false;
      }
    }
    return true;
  }

  async function handleSave(submit) {
    setError('');
    if (submit && !validate()) return;
    submit ? setSubmitting(true) : setSaving(true);

    try {
      const result = await upsertResponse({
        enrolment_id: enrolment.id,
        section,
        answers,
        status: submit ? 'Submitted' : 'Draft',
        submitted_at: submit ? new Date().toISOString() : null,
        form_snapshot: {
          questions: questions.map(q => ({
            id: q.id,
            section_title: q.section_title,
            prompt: q.prompt,
            type: q.type,
            show_justification: q.show_justification
          })),
          courseSpecific: courseQuestion ? {
            section_title: courseQuestion.section_title,
            prompt: courseQuestion.prompt
          } : null
        }
      }, existingResponse);
      onSaved(result);
      setSuccess(submit ? 'Submitted successfully.' : 'Draft saved.');
    } catch (err) {
      console.error('Self-Evaluation save failed:', err);
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  }

  const nonGradeQuestions = questions.filter(q => q.type !== 'GRADE');
  const gradeQuestion = questions.find(q => q.type === 'GRADE');

  return (
    <div>
      {nonGradeQuestions.map(q => {
        const answer = answers[q.id] || {};
        const choices = (() => { try { return JSON.parse(q.choices || '[]'); } catch { return []; } })();

        return (
          <div key={q.id} style={styles.questionCard}>
            {q.section_title && <div style={styles.sectionTitle}>{q.section_title}</div>}
            {q.section_description && (
              <div style={styles.sectionDesc}>{q.section_description}</div>
            )}
            <div style={styles.prompt}>{q.prompt}</div>

            {q.type === 'RADIO' && choices.length > 0 && (
              <div style={styles.choices}>
                {choices.map((choice, i) => (
                  <button
                    key={i}
                    type="button"
                    style={{
                      ...styles.choiceBtn,
                      ...(answer.value === choice ? styles.choiceBtnSelected : {})
                    }}
                    onClick={() => setAnswer(q.id, choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            )}

            {(q.type === 'TEXTAREA' || q.type === 'TEXT') && (
              <textarea
                style={styles.textarea}
                value={answer.value || ''}
                onChange={e => setAnswer(q.id, e.target.value)}
                rows={q.type === 'TEXTAREA' ? 6 : 2}
                placeholder="Your answer..."
              />
            )}

            {q.show_justification && (
              <div style={styles.justificationWrap}>
                <label style={styles.justLabel}>{q.justification_prompt || 'Justification:'}</label>
                <textarea
                  style={styles.textarea}
                  value={answer.justification || ''}
                  onChange={e => setAnswer(q.id, e.target.value, 'justification')}
                  rows={4}
                  placeholder="Your justification..."
                />
              </div>
            )}
          </div>
        );
      })}

      {courseQuestion && (
        <div style={styles.questionCard}>
          {courseQuestion.section_title && (
            <div style={styles.sectionTitle}>{courseQuestion.section_title}</div>
          )}
          <div style={styles.prompt}>{courseQuestion.prompt}</div>
          <textarea
            style={styles.textarea}
            value={answers['courseSpecific']?.value || ''}
            onChange={e => setAnswer('courseSpecific', e.target.value)}
            rows={6}
            placeholder="Your answer..."
          />
        </div>
      )}

      {gradeQuestion && (
        <div style={styles.questionCard}>
          {gradeQuestion.section_title && (
            <div style={styles.sectionTitle}>{gradeQuestion.section_title}</div>
          )}
          {gradeQuestion.section_description && (
            <div style={styles.sectionDesc}>{gradeQuestion.section_description}</div>
          )}
          <div style={styles.prompt}>{gradeQuestion.prompt}</div>
          {(() => {
            const choices = (() => { try { return JSON.parse(gradeQuestion.choices || '[]'); } catch { return []; } })();
            const answer = answers[gradeQuestion.id] || {};
            return choices.length > 0 ? (
              <div style={styles.choices}>
                {choices.map((choice, i) => (
                  <button
                    key={i}
                    type="button"
                    style={{
                      ...styles.choiceBtn,
                      ...(answer.value === choice ? styles.choiceBtnSelected : {})
                    }}
                    onClick={() => setAnswer(gradeQuestion.id, choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            ) : (
              <textarea
                style={styles.textarea}
                value={answer.value || ''}
                onChange={e => setAnswer(gradeQuestion.id, e.target.value)}
                rows={2}
                placeholder="Your suggested grade..."
              />
            );
          })()}
          {gradeQuestion.show_justification && (
            <div style={styles.justificationWrap}>
              <label style={styles.justLabel}>{gradeQuestion.justification_prompt || 'Justification:'}</label>
              <textarea
                style={styles.textarea}
                value={answers[gradeQuestion.id]?.justification || ''}
                onChange={e => setAnswer(gradeQuestion.id, e.target.value, 'justification')}
                rows={4}
                placeholder="Your justification..."
              />
            </div>
          )}
        </div>
      )}

      {error && <div role="alert" style={styles.error}>{error}</div>}
      {success && <div role="status" style={styles.success}>{success}</div>}

      <div style={styles.btnRow}>
        <button style={styles.secondaryBtn} disabled={saving || submitting} onClick={() => handleSave(false)}>
          {saving ? 'Saving...' : 'Save Draft'}
        </button>
        <button style={styles.submitBtn} disabled={saving || submitting} onClick={() => handleSave(true)}>
          {submitting ? 'Submitting...' : 'Submit'}
        </button>
      </div>
    </div>
  );
}

export default function StudentSelfEval({ session, course, enrolment, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [midCourseQuestion, setMidCourseQuestion] = useState(null);
  const [finalCourseQuestion, setFinalCourseQuestion] = useState(null);
  const [midResponse, setMidResponse] = useState(null);
  const [finalResponse, setFinalResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    Promise.all([
      fetchQuestions(),
      fetchCourseQuestion(course.id, 'Mid-Semester'),
      fetchCourseQuestion(course.id, 'Final'),
      fetchResponse(enrolment.id, 'Mid-Semester'),
      fetchResponse(enrolment.id, 'Final')
    ]).then(([q, mcq, fcq, mr, fr]) => {
      setQuestions(q);
      setMidCourseQuestion(mcq);
      setFinalCourseQuestion(fcq);
      setMidResponse(mr);
      setFinalResponse(fr);
    }).finally(() => setLoading(false));
  }, [course.id, enrolment.id]);

  const midStatus = getSectionStatus(course, 'Mid-Semester', session);
  const finalStatus = getSectionStatus(course, 'Final', session);

  if (loading) return <div style={styles.wrap}><div style={styles.muted}>Loading...</div></div>;

  if (activeSection) {
    const isMid = activeSection === 'Mid-Semester';
    const status = isMid ? midStatus : finalStatus;
    const response = isMid ? midResponse : finalResponse;
    const courseQuestion = isMid ? midCourseQuestion : finalCourseQuestion;
    const isSubmitted = response?.status === 'Submitted';
    const closeDate =
      getSectionCloseDate(
        course,
        activeSection
      );

    return (
      <div style={styles.wrap}>
        <div style={styles.content}>
          <button style={styles.backLink} onClick={() => setActiveSection(null)}>
            ← Back
          </button>
          <h2 style={styles.heading}>{activeSection} Self-Evaluation</h2>

          {isSubmitted ? (
            <div>
              <div style={styles.submittedBanner}>
                ✓ Submitted on {formatDate(response.submitted_at?.slice(0, 10))}
                {response.final_grade && (
                  <span style={styles.finalGrade}> · Final grade: <strong>{response.final_grade}</strong></span>
                )}
              </div>
              <div style={styles.muted}>Your responses have been submitted and cannot be edited.</div>
            </div>
          ) : (
            <>
              {status === 'open' && closeDate && (
                <div style={styles.openBanner}>
                  Open until {formatDate(closeDate)}
                </div>
              )}
              {status !== 'open' && (
                <div style={styles.closedBanner}>
                  {status === 'not_open' ? 'This evaluation is not yet open.' : 'This evaluation has closed.'}
                </div>
              )}
              {status === 'open' && (
                <SelfEvalForm
                  section={activeSection}
                  course={course}
                  enrolment={enrolment}
                  questions={questions}
                  courseQuestion={courseQuestion}
                  existingResponse={response}
                  onSaved={result => {
                    if (isMid) setMidResponse(result);
                    else setFinalResponse(result);
                  }}
                />
              )}
              {status !== 'open' && response && (
                <div style={styles.draftNote}>
                  You have a draft saved but the evaluation window is not open.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>Self-Evaluation</h2>

        {[
          { section: 'Mid-Semester', status: midStatus, response: midResponse, openDate: course.mid_eval_open_date, closeDate: getSectionCloseDate(course,'Mid-Semester') },
          { section: 'Final', status: finalStatus, response: finalResponse, openDate: course.final_eval_open_date, closeDate: getSectionCloseDate(course,'Final') }
        ].map(({ section, status, response, openDate, closeDate }) => {
          const isSubmitted = response?.status === 'Submitted';
          const hasDraft = response && !isSubmitted;

          return (
            <button
              key={section}
              style={styles.sectionBtn}
              onClick={() => setActiveSection(section)}
            >
              <div style={styles.sectionBtnLeft}>
                <div style={styles.sectionBtnTitle}>{section} Self-Evaluation</div>
                <div style={styles.sectionBtnMeta}>
                  {status === 'open' && closeDate && `Open · closes ${formatDate(closeDate)}`}
                  {status === 'not_open' && openDate && `Opens ${formatDate(openDate)}`}
                  {status === 'closed' && 'Closed'}
                  {status === 'not_configured' && 'Dates not set'}
                </div>
              </div>
              <div style={styles.sectionBtnRight}>
                {isSubmitted ? (
                  <span style={styles.submittedPill}>✓ Submitted</span>
                ) : hasDraft ? (
                  <span style={styles.draftPill}>Draft saved</span>
                ) : status === 'open' ? (
                  <span style={styles.openPill}>Open</span>
                ) : status === 'not_open' ? (
                  <span style={styles.notOpenPill}>Not yet open</span>
                ) : (
                  <span style={styles.closedPill}>Closed</span>
                )}
              </div>
              <span style={styles.arrow}>›</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  wrap: { minHeight: '60vh', background: '#f4f6f8' },
  content: { padding: '20px', maxWidth: '640px', margin: '0 auto' },
  muted: { color: '#66788a', fontSize: '14px', padding: '8px 0' },
  heading: { margin: '0 0 20px', fontSize: '22px', color: '#1f2933', fontWeight: '700' },
  backLink: {
    border: 'none', background: 'none', color: '#215b7d',
    fontSize: '14px', fontWeight: '600', cursor: 'pointer',
    padding: '0 0 16px', display: 'block'
  },
  sectionBtn: {
    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
    padding: '16px', background: 'white', border: '1px solid #d7dee5',
    borderRadius: '10px', cursor: 'pointer', textAlign: 'left', marginBottom: '10px'
  },
  sectionBtnLeft: { flex: 1 },
  sectionBtnTitle: { fontWeight: '700', fontSize: '15px', color: '#1f2933', marginBottom: '4px' },
  sectionBtnMeta: { fontSize: '13px', color: '#66788a' },
  sectionBtnRight: { flexShrink: 0 },
  arrow: { color: '#66788a', fontSize: '20px', flexShrink: 0 },
  submittedPill: {
    background: '#e7f5ed', color: '#216e45', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  draftPill: {
    background: '#fff4d7', color: '#8a5b00', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  openPill: {
    background: '#e7f1f7', color: '#215b7d', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  notOpenPill: {
    background: '#f0f4f7', color: '#66788a', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  closedPill: {
    background: '#faeaea', color: '#a12a2a', borderRadius: '999px',
    padding: '3px 10px', fontSize: '12px', fontWeight: '700'
  },
  questionCard: {
    background: 'white', border: '1px solid #d7dee5', borderRadius: '10px',
    padding: '18px', marginBottom: '12px'
  },
  sectionTitle: { fontWeight: '700', fontSize: '16px', color: '#1f2933', marginBottom: '6px' },
  sectionDesc: { fontSize: '14px', color: '#66788a', lineHeight: '1.5', marginBottom: '10px' },
  prompt: { fontSize: '15px', color: '#1f2933', lineHeight: '1.6', marginBottom: '12px' },
  choices: { display: 'flex', flexDirection: 'column', gap: '8px' },
  choiceBtn: {
    width: '100%', textAlign: 'left', padding: '12px 16px',
    border: '2px solid #d7dee5', borderRadius: '8px', background: 'white',
    fontSize: '15px', cursor: 'pointer', color: '#1f2933'
  },
  choiceBtnSelected: {
    borderColor: '#215b7d', background: '#e7f1f7', color: '#215b7d', fontWeight: '600'
  },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 12px', fontSize: '15px', boxSizing: 'border-box',
    background: 'white', resize: 'vertical', lineHeight: '1.6'
  },
  justificationWrap: { marginTop: '12px' },
  justLabel: { display: 'block', fontSize: '13px', fontWeight: '700', color: '#66788a', marginBottom: '6px' },
  btnRow: { display: 'flex', gap: '10px', marginTop: '16px' },
  secondaryBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '8px', padding: '11px 20px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
  },
  submitBtn: {
    flex: 1, background: '#215b7d', color: 'white', border: 'none',
    borderRadius: '8px', padding: '11px', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
  },
  submittedBanner: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '10px',
    padding: '14px 18px', fontSize: '15px', color: '#216e45', marginBottom: '12px'
  },
  finalGrade: { color: '#1f2933' },
  openBanner: {
    background: '#e7f1f7', border: '1px solid #b0cfe0', borderRadius: '8px',
    padding: '10px 14px', fontSize: '14px', color: '#215b7d', marginBottom: '16px'
  },
  closedBanner: {
    background: '#f0f4f7', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '10px 14px', fontSize: '14px', color: '#66788a', marginBottom: '16px'
  },
  draftNote: {
    background: '#fff4d7', border: '1px solid #ead494', borderRadius: '8px',
    padding: '10px 14px', fontSize: '14px', color: '#8a5b00', marginTop: '12px'
  },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  }
};